import { Component, Inject, OnInit, Optional } from '@angular/core';
import { KeyValue } from '@angular/common';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { EconomyService, ShiftTypeItem } from '@app/services/economy.service';
import { ShiftCalendar, ShiftCalendarDay, ShiftCalendarChange, ShiftService, ShiftViewGet } from '@app/services/shift.service';
import { AgCartesianChartOptions } from 'ag-charts-community';

@Component({
  selector: 'tt-shift-view',
  templateUrl: './shift-view.component.html',
  styleUrls: ['./shift-view.component.css'],
  standalone: false,
})
export class ShiftViewComponent implements OnInit {
    public page?: ShiftViewGet;
    public labourNo = '';
    public yearSelector = 'this';
    public selectedView = 'CALENDAR';
    public timePeriod = 'WEEK';
    public calendar: ShiftCalendar = { dates: [], months: {}, monthnames: {}, dayletters: {} };
    public shifts: CalendarShiftItem[] = [];
    public selectedShift?: CalendarShiftItem;
    public totalShiftDays = 0;
    public grid?: GridOptions;
    public graph?: AgCartesianChartOptions;
    public saving = false;
    public loading = false;
    public numericKeyOrder = (a: KeyValue<string, unknown>, b: KeyValue<string, unknown>): number => Number(a.key) - Number(b.key);
    public weekOrder = (a: KeyValue<string, Record<string, ShiftCalendarDay>>, b: KeyValue<string, Record<string, ShiftCalendarDay>>): number => {
        const firstDate = (days: Record<string, ShiftCalendarDay>): string => Object.values(days).map(day => day.systemdate).filter(Boolean).sort()[0] ?? '';
        return firstDate(a.value).localeCompare(firstDate(b.value));
    };

    constructor(
        private readonly shift: ShiftService,
        private readonly economy: EconomyService,
        private readonly modal: ModalService,
        private readonly translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) { this.navigator = useNavigatorFor(navigator, this); }

    public get year(): number {
        return new Date().getFullYear() + (this.yearSelector === 'prev' ? -1 : this.yearSelector === 'next' ? 1 : 0);
    }

    public async ngOnInit(): Promise<void> {
        this.labourNo = String(this.navigator?.params['labour_no'] ?? '');
        this.loading = true;
        try {
            const [page, shifts] = await Promise.all([this.shift.getView({ labour_no: this.labourNo }), this.economy.getShiftTypes({ labour_no: this.labourNo })]);
            this.page = page;
            this.shifts = shifts.filter(shift => Number(shift.item_validok) > 0).map(shift => ({ ...shift, shiftDays: 0 }));
            this.grid = new Grid()
                .setRememberId(page.view_id || '')
                .setLoadDataFunction({ method: Number(page.p2_datatask_keyno) || null, parameters: () => ({ year: this.year, labour_no: this.labourNo }) })
                .setSpecialFunc({ newTab: true })
                .setKendoConfig({ height: null, selectable: false });
            await this.loadCalendar();
        } finally { this.loading = false; }
    }

    private async loadCalendar(): Promise<void> {
        this.calendar = await this.shift.getCalendar({ year: this.year, labour_no: this.labourNo }, this.shifts);
        this.summarize();
    }

    public setShiftSelected(index: number): void { this.selectedShift = this.shifts[index]; }

    private get days(): ShiftCalendarDay[] {
        return Object.values(this.calendar.months).flatMap(weeks => Object.values(weeks).flatMap(days => Object.values(days)));
    }

    public tableCellClick(systemdate: string): void { this.applyShift(this.days.filter(day => day.systemdate === systemdate && systemdate !== '')); }
    public monthClick(month: string): void { this.applyShift(this.days.filter(day => String(day.month_no) === month)); }
    public dayClick(weekday: string, month: string): void { this.applyShift(this.days.filter(day => String(day.week_day) === weekday && String(day.month_no) === month)); }
    public weekClick(week: string): void { this.applyShift(this.days.filter(day => String(day.week_no) === week.trim())); }

    private applyShift(days: ShiftCalendarDay[]): void {
        if (!this.selectedShift || this.saving || this.loading) return;
        for (const day of days) {
            if (!day.systemdate || String(day.is_protected) !== '0') continue;
            const remove = day.shift_id === this.selectedShift.item_id;
            day.shift_id = remove ? '' : this.selectedShift.item_id;
            day.background_color_hex = remove ? day.original_background_color_hex ?? '' : this.selectedShift.background_color_hex;
            day.text_color_hex = remove ? day.original_text_color_hex ?? '' : this.selectedShift.text_color_hex;
            day.shift_background_color_hex = day.background_color_hex;
            day.shift_text_color_hex = day.text_color_hex;
            day.ischanged = '1';
        }
        this.summarize();
    }

    public summarize(): void {
        this.totalShiftDays = 0;
        this.shifts.forEach(shift => shift.shiftDays = 0);
        for (const day of this.days) {
            if (!day.systemdate || !day.shift_id) continue;
            const shift = this.shifts.find(shift => shift.item_id === day.shift_id);
            if (shift) shift.shiftDays++;
            this.totalShiftDays++;
        }
    }

    private async saveCalendar(): Promise<boolean> {
        const records: ShiftCalendarChange[] = this.days.filter(day => day.systemdate && day.ischanged === '1').map(({ systemdate, shift_id, labour_no }) => ({ systemdate, shift_id, labour_no }));
        const response = await this.shift.saveCalendar(records);
        if (String(response.errorcode) !== '0') {
            await this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage, buttons: [{ label: await this.translate.translate('ok'), cssClass: 'btn-warning', close: true }] });
            return false;
        }
        this.days.forEach(day => day.ischanged = '0');
        return true;
    }

    public async saveChanges(): Promise<void> {
        if (this.saving || this.loading) return;
        this.saving = true;
        try { if (await this.saveCalendar()) await this.navigator?.back(); }
        finally { this.saving = false; }
    }

    public async selectYear(value: string): Promise<void> {
        if (this.saving || this.loading || value === this.yearSelector) return;
        this.loading = true;
        try {
            if (this.selectedView === 'CALENDAR' && !await this.saveCalendar()) return;
            this.yearSelector = value;
            await this.loadSelectedView();
        } finally { this.loading = false; }
    }

    public async selectView(value: string): Promise<void> {
        if (this.saving || this.loading || value === this.selectedView) return;
        this.loading = true;
        try {
            if (this.selectedView === 'CALENDAR' && !await this.saveCalendar()) return;
            this.selectedView = value;
            await this.loadSelectedView();
        } finally { this.loading = false; }
    }

    public async selectTimePeriod(value: string): Promise<void> {
        if (this.loading || this.saving) return;
        this.timePeriod = value;
        this.loading = true;
        try { await this.loadGraph(); } finally { this.loading = false; }
    }

    private async loadSelectedView(): Promise<void> {
        if (this.selectedView === 'CALENDAR') await this.loadCalendar();
        else if (this.selectedView === 'GRAPH') await this.loadGraph();
        else await this.grid?.gridfunc?.rebind();
    }

    private async loadGraph(): Promise<void> {
        const records = await this.shift.getGraph({ year: this.year, labour_no: this.labourNo, bg_timeperiod: this.timePeriod });
        this.graph = {
            data: records.map(record => ({ ...record, quantity: Number(record.quantity) })),
            series: [{ type: 'line', xKey: 'item_name', yKey: 'quantity' }],
            axes: [{ type: 'category', position: 'bottom' }, { type: 'number', position: 'left' }],
        };
    }
}

interface CalendarShiftItem extends ShiftTypeItem { shiftDays: number; }
